import type * as monacoTypes from 'monaco-editor';

export type PythonAutocompleteItem = {
  display: string;
  token: string;
};

// Per-model autocomplete data (weak keyed by ITextModel so GC can reclaim)
export const pythonModelAutocomplete = new WeakMap<monacoTypes.editor.ITextModel, PythonAutocompleteItem[]>();

// Python keywords
const PYTHON_KEYWORDS = [
  'False',
  'None',
  'True',
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'try',
  'while',
  'with',
  'yield',
];

// Python built-in functions
const PYTHON_BUILTINS = [
  'abs',
  'all',
  'any',
  'bin',
  'bool',
  'bytes',
  'callable',
  'chr',
  'dict',
  'dir',
  'divmod',
  'enumerate',
  'filter',
  'float',
  'format',
  'frozenset',
  'getattr',
  'hasattr',
  'hash',
  'hex',
  'id',
  'input',
  'int',
  'isinstance',
  'issubclass',
  'iter',
  'len',
  'list',
  'map',
  'max',
  'min',
  'next',
  'object',
  'oct',
  'open',
  'ord',
  'pow',
  'print',
  'range',
  'repr',
  'reversed',
  'round',
  'set',
  'setattr',
  'slice',
  'sorted',
  'str',
  'sum',
  'tuple',
  'type',
  'vars',
  'zip',
];

// One provider for the whole app session
let pythonProviderRegistered = false;

export const ensurePythonProvider = (monaco: typeof import('monaco-editor')) => {
  if (pythonProviderRegistered) return;

  // Register completion provider for Python
  monaco.languages.registerCompletionItemProvider('python', {
    provideCompletionItems: (model, position) => {
      const wordRange = model.getWordAtPosition(position);
      const range = wordRange
        ? {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: wordRange.startColumn,
            endColumn: wordRange.endColumn,
          }
        : {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column,
            endColumn: position.column,
          };

      const suggestions: import('monaco-editor').languages.CompletionItem[] = [];

      // Add context variables from the model (highest priority)
      const contextTokens = pythonModelAutocomplete.get(model) ?? [];
      for (const item of contextTokens) {
        suggestions.push({
          label: item.display,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: item.token,
          detail: item.token,
          documentation: `Context variable: ${item.token}`,
          range,
          sortText: `0_${item.display}`, // Sort first
        });
      }

      // Add Python keywords (second priority)
      for (const keyword of PYTHON_KEYWORDS) {
        suggestions.push({
          label: keyword,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: keyword,
          detail: 'keyword',
          range,
          sortText: `1_${keyword}`, // Sort second
        });
      }

      // Add Python built-ins (third priority)
      for (const builtin of PYTHON_BUILTINS) {
        suggestions.push({
          label: builtin,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: builtin,
          detail: 'built-in',
          range,
          sortText: `2_${builtin}`, // Sort third
        });
      }

      return { suggestions };
    },
  });

  pythonProviderRegistered = true;
};
